import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TradingViewWidget } from './widgets/TradingViewWidget';
import { EconomicMap } from './widgets/EconomicMap';
type TabId = 'stocks' | 'crypto' | 'calendar' | 'map';
const tabs: {
  id: TabId;
  label: string;
  hint: string;
}[] = [{
  id: 'stocks',
  label: 'Mapa SPX500',
  hint: 'Rotación sectorial en vivo'
}, {
  id: 'crypto',
  label: 'Mapa Cripto',
  hint: 'Variación 24h por market cap'
}, {
  id: 'calendar',
  label: 'Calendario',
  hint: 'Eventos macro de alto impacto'
}, {
  id: 'map',
  label: 'Mapa Económico',
  hint: 'Datos macro por país'
}];
const STOCK_HEATMAP = {
  dataSource: 'SPX500',
  blockSize: 'market_cap_basic',
  blockColor: 'change',
  grouping: 'sector',
  locale: 'en',
  symbolUrl: '',
  colorTheme: 'dark',
  exchanges: [],
  hasTopBar: false,
  isDataSetEnabled: false,
  isZoomEnabled: true,
  hasSymbolTooltip: true,
  isMonoSize: false,
  width: '100%',
  height: '100%'
};
const CRYPTO_HEATMAP = {
  dataSource: 'Crypto',
  blockSize: 'market_cap_calc',
  blockColor: '24h_close_change|5',
  locale: 'en',
  symbolUrl: '',
  colorTheme: 'dark',
  hasTopBar: false,
  isDataSetEnabled: false,
  isZoomEnabled: true,
  hasSymbolTooltip: true,
  isMonoSize: false,
  width: '100%',
  height: '100%'
};
const CALENDAR = {
  colorTheme: 'dark',
  isTransparent: false,
  locale: 'en',
  countryFilter: 'ar,au,br,ca,cn,fr,de,in,id,it,jp,kr,mx,ru,sa,za,tr,gb,us,eu',
  importanceFilter: '-1,0,1',
  width: '100%',
  height: '100%'
};
export function MarketsSection() {
  const [active, setActive] = useState<TabId>('stocks');
  const activeTab = tabs.find(tab => tab.id === active) ?? tabs[0];
  return <section id="mercados" className="border-b border-line" data-id="element-14" data-mp="1a8d73zc23mzj">
      <div className="mx-auto w-full max-w-[1400px] px-5 py-16 sm:px-6 lg:px-10 lg:py-24" data-id="element-15" data-mp="1aapxn0vvr5o7">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-16" data-mp="3pyg57sxq3aeo">
          <div className="max-w-2xl" data-id="element-17" data-mp="3v0f9d51aebco">
            <div className="mb-4 flex items-center gap-3" data-id="element-18" data-mp="3awq47w3w03su">
              <span className="h-px w-6 bg-gold-vip sm:w-8" data-id="element-19" data-mp="3aw83am6a51el" />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-vip sm:text-[11px]" data-id="element-20" data-mp="36umjshz8povf">
                Market Data · Live
              </span>
            </div>
            <h2 className="text-[22px] font-semibold tracking-tight text-white sm:text-3xl" data-id="element-21" data-mp="2x5kzwgpkqnc6">
              Contexto antes del gatillo.
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-white/50 sm:text-[15px]" data-id="element-22" data-mp="2s7t7zmoxj17z">
              El rango de apertura no se opera a ciegas. Revisa flujo sectorial,
              cripto y los eventos macro del día antes de habilitar el
              protocolo.
            </p>
          </div>

          <p className="font-mono text-[11px] text-white/30 lg:text-right" data-id="element-23" data-mp="2e81stimofese">
            {activeTab.hint}
          </p>
        </div>

        <div role="tablist" aria-label="Paneles de mercado" className="mt-8 flex gap-1 overflow-x-auto border-b border-line pb-px" data-id="element-24" data-mp="cf433vo752bo">
          {tabs.map(tab => {
          const isActive = tab.id === active;
          return <button key={tab.id} role="tab" type="button" aria-selected={isActive} onClick={() => setActive(tab.id)} className={`relative whitespace-nowrap px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-vip sm:px-4 sm:text-[12px] ${isActive ? 'text-cyan-vip' : 'text-white/40 hover:text-white/75'}`} data-id="element-25" data-mp="ovv9qi1cxrsw">
                {tab.label}
                {isActive ? <motion.span layoutId="markets-tab-underline" className="absolute inset-x-2 -bottom-px h-px bg-cyan-vip" transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-26" data-mp="1mcbyaiq9vmqk" /> : null}
              </button>;
        })}
        </div>

        <motion.div key={active} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="tabpanel" className="mt-6 h-[440px] overflow-hidden rounded-lg border border-line bg-ink-800 sm:h-[520px] lg:h-[620px]" data-id="element-27" data-mp="1yvppc5bfam7f">
          {active === 'stocks' ? <TradingViewWidget scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-stock-heatmap.js" config={STOCK_HEATMAP} copyright={{
          href: 'https://www.tradingview.com/heatmap/stock/',
          label: 'Stock Heatmap'
        }} data-id="element-28" data-mp="22v87wufrul73" /> : null}

          {active === 'crypto' ? <TradingViewWidget scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-crypto-coins-heatmap.js" config={CRYPTO_HEATMAP} copyright={{
          href: 'https://www.tradingview.com/heatmap/crypto/',
          label: 'Crypto Heatmap'
        }} data-id="element-29" data-mp="2mj7avj43go1y" /> : null}

          {active === 'calendar' ? <TradingViewWidget scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-events.js" config={CALENDAR} copyright={{
          href: 'https://www.tradingview.com/economic-calendar/',
          label: 'Economic Calendar'
        }} data-id="element-30" data-mp="31a3knxhpg5zi" /> : null}

          {active === 'map' ? <EconomicMap data-id="element-31" data-mp="2hkt3sg683ezt" /> : null}
        </motion.div>
      </div>
    </section>;
}